import { create } from 'zustand';

import { configureAudioSession, type AudioSessionResult } from './audioSession';
import { repairAudio } from './audioRepair';
import {
  audioState,
  ensureRunning,
  isUnlocked,
  onAudioTransition,
  unlockAudio,
  unlockOnFirstGesture,
  type AudioState,
} from './audioUnlock';
import { warmUpTts } from './warmup';

/** Whether `init()` has run. Separate from any result it produced — see the comment in `init`. */
let initialised = false;

/**
 * What the UI needs to know about audio, and nothing more.
 *
 * `needsUnlock` drives the `Ton aktivieren – antippen` banner. It is set only after recovery has
 * actually failed, never pre-emptively: a banner shown while audio is fine trains the person to
 * ignore it, and then it is useless on the day it matters.
 */
interface AudioStatusState {
  state: AudioState;
  unlocked: boolean;
  needsUnlock: boolean;
  /** Set when playback reported no progress — suspect Control Center mute. */
  silentPlayback: boolean;
  sessionConfigured: AudioSessionResult | null;
  /** How many repairs this page load has attempted, for the Diagnose panel. */
  repairs: number;
  init: () => void;
  /** From the banner tap: a real gesture, so this is where unlock can actually succeed. */
  unlockFromGesture: () => Promise<void>;
  /**
   * The full repair, from inside a tap (`SPK-19`).
   *
   * Here rather than imported by the component, so no surface knows what repairing audio involves
   * (`CON-10`). Returns whether the person has a voice again — which is what the caller has to decide
   * between re-speaking and escalating on.
   */
  repairFromGesture: () => Promise<boolean>;
  /** Called before playback; surfaces the banner if the context cannot be revived. */
  requireRunning: () => Promise<boolean>;
  reportSilentPlayback: (silent: boolean) => void;
}

export const useAudioStatus = create<AudioStatusState>((set, get) => ({
  state: 'unavailable',
  unlocked: false,
  needsUnlock: false,
  silentPlayback: false,
  sessionConfigured: null,
  repairs: 0,

  init: () => {
    /**
     * Idempotence on a flag of its own, not on `sessionConfigured` (C82).
     *
     * It used to guard on `sessionConfigured !== null` — a *result*, which meant that once the session
     * had been configured once it could never be configured again. `SHL-10` needs to re-set it on every
     * return, so the guard and the result had to stop being the same thing.
     */
    if (initialised) return;
    initialised = true;
    set({ sessionConfigured: configureAudioSession() });

    unlockOnFirstGesture();

    onAudioTransition((transition) => {
      set({ state: transition.to, unlocked: isUnlocked() });
      // A transition away from running is not itself a problem — the watchdog may well win.
      // Only a failed requireRunning() raises the banner.
      if (transition.to === 'running') set({ needsUnlock: false });
    });

    /**
     * Re-check when the app comes back (C71).
     *
     * iOS suspends the context while the app is backgrounded, and `resume()` outside a gesture does not
     * reliably work — so coming back to the foreground is the moment to find out whether we still have a
     * voice, and to ask for a tap if we do not. Without this, `requireRunning()` had **no callers at all**:
     * the one function that can raise the `Ton aktivieren` banner was never called, so the banner could not
     * appear in the one situation it exists for, and the app stayed silent until it was relaunched.
     *
     * `pageshow` as well as `visibilitychange`, because a PWA restored from the page cache fires the former
     * and not always the latter.
     */
    if (typeof document !== 'undefined') {
      const recheck = (): void => {
        if (document.visibilityState !== 'visible') return;
        void get().requireRunning();

        /**
         * Two more once-per-page facts that a nap invalidates, and neither announces its own loss
         * (`SHL-10`, C82).
         *
         * A reverted audio session means silent mode mutes the person's voice again — a mute app that
         * looks fine, which is the failure this project distrusts most. A cold Functions host means the
         * first sentence costs 2.09 s instead of 0.44 s, measured, and lands on the sentence somebody
         * says to a person standing in the doorway.
         */
        set({ sessionConfigured: configureAudioSession() });
        // The TTL decides whether this actually goes, and concurrent asks fan into one request —
        // `visibilitychange` and `pageshow` can both fire on the same return.
        void warmUpTts();
      };
      document.addEventListener('visibilitychange', recheck);
      window.addEventListener('pageshow', recheck);
    }

    set({ state: audioState(), unlocked: isUnlocked() });
  },

  unlockFromGesture: async () => {
    const ok = await unlockAudio();
    set({ unlocked: ok, state: audioState(), needsUnlock: !ok });
  },

  repairFromGesture: async () => {
    const outcome = await repairAudio();
    set((s) => ({
      repairs: s.repairs + 1,
      state: audioState(),
      unlocked: isUnlocked(),
      needsUnlock: !outcome.ok,
    }));
    if (!outcome.ok) console.warn('[audio] repair did not restore the voice', outcome.reason);
    return outcome.ok;
  },

  requireRunning: async () => {
    const ok = await ensureRunning();
    set({ state: audioState(), unlocked: isUnlocked(), needsUnlock: !ok });
    return ok;
  },

  reportSilentPlayback: (silent) => set({ silentPlayback: silent }),
}));
